// Roster: 32 players across 4 divisions, with player swapping.
//
// SWAPPING A PLAYER
// Two options, deliberately kept separate because they mean different things:
//
//  1. "Replace" -- a season-long substitution. The new player inherits the
//     outgoing player's slot in every future match, so the round robin
//     stays intact. Completed results stay with whoever actually played.
//
//  2. "Swap divisions" -- move two players between divisions. This
//     invalidates the generated schedule, so the app warns you and you
//     regenerate from the Schedule tab.

import React, { useMemo, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Plus, Trash2, ArrowLeftRight, UserPlus, AlertTriangle, Save } from 'lucide-react';
import DivisionBadge from './DivisionBadge';
import ReadOnlyNotice from './ReadOnlyNotice';
import {
  DIVISIONS, PLAYERS_PER_DIVISION,
  TEE_TIME_PREFERENCES, DEFAULT_TEE_TIME_PREFERENCE, TEE_TIME_PREFERENCE_LABELS,
} from '../utils/constants';
import { formatHandicap, parseHandicapInput } from '../utils/formatting';
import { uid } from '../utils/helpers';
import { tierOptions, tierCoverageGaps } from '../utils/teeTiers';

export default function RosterTab({
  golfers, teeSets, courses, matches, isAdmin,
  onSaveGolfer, onDeleteGolfer, onReplacePlayer, onSwapDivisions,
}) {
  const [drafts, setDrafts] = useState({});
  const [adding, setAdding] = useState({
    name: '', division: 'Woods', handicap_index: '', ghin_id: '',
    tee_time_pref: DEFAULT_TEE_TIME_PREFERENCE,
  });
  const [replace, setReplace] = useState({ outId: '', name: '', handicap_index: '', ghin_id: '' });
  const [swap, setSwap] = useState({ aId: '', bId: '' });

  const active = useMemo(() => (golfers || []).filter(g => g.active !== false), [golfers]);
  const inactive = useMemo(() => (golfers || []).filter(g => g.active === false), [golfers]);

  const byDivision = useMemo(() => {
    const m = {};
    DIVISIONS.forEach(d => { m[d] = active.filter(g => g.division === d); });
    Object.values(m).forEach(list =>
      list.sort((a, b) => Number(a.handicap_index ?? 99) - Number(b.handicap_index ?? 99)));
    return m;
  }, [active]);

  const merged = (g) => ({ ...g, ...(drafts[g.id] || {}) });
  const dirty = (g) => !!drafts[g.id] && Object.keys(drafts[g.id]).length > 0;
  const edit = (id, field, value) =>
    setDrafts(d => ({ ...d, [id]: { ...(d[id] || {}), [field]: value } }));

  const save = async (g) => {
    const m = merged(g);
    const payload = {
      ...g,
      name: ((m.name ?? g.name) || '').trim(),
      division: m.division ?? g.division,
      ghin_id: (m.ghin_id ?? g.ghin_id) || null,
      default_tee_set_id: (m.default_tee_set_id ?? g.default_tee_set_id) || null,
      default_tee_tier: (() => {
        const v = m.default_tee_tier ?? g.default_tee_tier;
        return v === '' || v === null || v === undefined ? null : Number(v);
      })(),
      handicap_index: m.handicap_index === undefined
        ? g.handicap_index
        : parseHandicapInput(m.handicap_index),
      // Always store a concrete value so the column is never ambiguous.
      tee_time_pref: (m.tee_time_pref ?? g.tee_time_pref) || DEFAULT_TEE_TIME_PREFERENCE,
      points_adjust: m.points_adjust === undefined ? (g.points_adjust || 0) : Number(m.points_adjust || 0),
    };
    delete payload.created_at; delete payload.updated_at;
    await onSaveGolfer(payload);
    setDrafts(d => { const c = { ...d }; delete c[g.id]; return c; });
  };

  // A tee TIER spans every course: tier 1 is White at Champions Pointe and
  // Black at Covered Bridge. Players pick the tier once, not a tee per course.
  const tiers = useMemo(() => tierOptions(teeSets, courses), [teeSets, courses]);
  const tierGaps = useMemo(() => tierCoverageGaps(teeSets, courses), [teeSets, courses]);
  const noTierCount = useMemo(
    () => active.filter(g => g.default_tee_tier === null || g.default_tee_tier === undefined).length,
    [active]);

  // Anything not explicitly 'late' is early, so an unset column is early.
  const latePlayers = useMemo(
    () => active.filter(g => String(g.tee_time_pref || '').toLowerCase() === 'late'),
    [active]);

  const sizeWarnings = DIVISIONS
    .filter(d => byDivision[d].length !== PLAYERS_PER_DIVISION)
    .map(d => `${d} has ${byDivision[d].length} players (needs ${PLAYERS_PER_DIVISION})`);

  return (
    <div className="mt-4 space-y-6">
      {!isAdmin && <ReadOnlyNotice>Roster is view-only. Unlock admin to edit.</ReadOnlyNotice>}

      {sizeWarnings.length > 0 && (
        <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <p className="font-medium">Divisions are not balanced.</p>
            <ul className="mt-1 list-inside list-disc">
              {sizeWarnings.map(w => <li key={w}>{w}</li>)}
            </ul>
            <p className="mt-1">
              A 7-round round robin needs exactly 8 players per division - one round for each of the
              7 regular season dates. Fix this before generating the schedule.
            </p>
          </div>
        </div>
      )}

      <div className="rounded-md border border-sky-200 bg-sky-50 p-3 text-sm text-sky-900">
        <p className="font-medium">Tee is a level, not a colour</p>
        <p className="mt-1">
          A player picks one tee tier and the app uses the matching tee at whichever course they
          are playing that week:
        </p>
        <ul className="mt-1 space-y-0.5">
          {tiers.map(t => (
            <li key={t.value}>
              <strong>{t.label}</strong> &mdash; {t.detail}
            </li>
          ))}
        </ul>
        <p className="mt-1 text-sky-800">
          Individual matches can still override this from the Tee Sheet or Results tab.
        </p>
      </div>

      {latePlayers.length > 0 && (
        <div className="rounded-md border border-indigo-200 bg-indigo-50 p-3 text-sm text-indigo-900">
          <p className="font-medium">
            {latePlayers.length} player{latePlayers.length === 1 ? '' : 's'} requested a late tee time.
          </p>
          <p className="mt-1">
            {latePlayers.map(g => g.name).join(', ')}
          </p>
          <p className="mt-1 text-indigo-800">
            Their foursomes are placed in the latest slots when you generate the schedule. A tee time
            holds four players and two matches, so one late player sends the whole group late.
          </p>
        </div>
      )}

      {tierGaps.length > 0 && (
        <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <p className="font-medium">A tee tier is missing at one or more courses.</p>
            <ul className="mt-1 list-inside list-disc">
              {tierGaps.map((gap, i) => (
                <li key={i}>
                  Tier {gap.tier} has no tee at {gap.course.name}
                </li>
              ))}
            </ul>
            <p className="mt-1">
              Players on that tier will fall back to the first tee at that course. Assign the tier
              on the Courses tab.
            </p>
          </div>
        </div>
      )}

      {noTierCount > 0 && (
        <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            {noTierCount} player{noTierCount === 1 ? ' has' : 's have'} no tee tier set. They will
            default to the first tee at each course until you pick one.
          </span>
        </div>
      )}

      {DIVISIONS.map(division => (
        <Card key={division}>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <DivisionBadge division={division} />
                <span className="text-sm font-normal text-gray-500">
                  {byDivision[division].length} players
                </span>
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-xs uppercase tracking-wide text-gray-500">
                    <th className="py-2 pr-2">Player</th>
                    <th className="py-2 pr-2 w-24">Index</th>
                    <th className="py-2 pr-2 w-28">GHIN</th>
                    <th className="py-2 pr-2 w-44" title="Tee level, applied at every course">Tee</th>
                    <th className="py-2 pr-2 w-28" title="Late players are scheduled in the latest tee times available">Tee time</th>
                    <th className="py-2 pr-2 w-32">Division</th>
                    <th className="py-2 pr-2 w-20">Adj pts</th>
                    {isAdmin && <th className="py-2 w-28"></th>}
                  </tr>
                </thead>
                <tbody>
                  {byDivision[division].map(g => {
                    const m = merged(g);
                    const hdcpValue = m.handicap_index === undefined
                      ? formatHandicap(g.handicap_index).replace('--', '')
                      : m.handicap_index;
                    return (
                      <tr key={g.id} className="border-b last:border-0">
                        <td className="py-1.5 pr-2">
                          <Input className="h-8" disabled={!isAdmin} value={m.name ?? g.name ?? ''}
                            onChange={e => edit(g.id, 'name', e.target.value)} />
                        </td>
                        <td className="py-1.5 pr-2">
                          <Input className="h-8" disabled={!isAdmin} value={hdcpValue}
                            placeholder="8.1 or +1.5"
                            onChange={e => edit(g.id, 'handicap_index', e.target.value)} />
                        </td>
                        <td className="py-1.5 pr-2">
                          <Input className="h-8" disabled={!isAdmin} value={m.ghin_id ?? g.ghin_id ?? ''}
                            onChange={e => edit(g.id, 'ghin_id', e.target.value)} />
                        </td>
                        <td className="py-1.5 pr-2">
                          <select
                            className="h-8 w-full rounded-md border border-gray-300 bg-white px-2 text-sm disabled:opacity-60"
                            disabled={!isAdmin}
                            value={m.default_tee_tier ?? g.default_tee_tier ?? ''}
                            onChange={e => edit(g.id, 'default_tee_tier', e.target.value)}
                            title={tiers.find(t => String(t.value) === String(m.default_tee_tier ?? g.default_tee_tier))?.detail || ''}
                          >
                            <option value="">(not set)</option>
                            {tiers.map(t => (
                              <option key={t.value} value={t.value}>{t.label}</option>
                            ))}
                          </select>
                        </td>
                        <td className="py-1.5 pr-2">
                          <select
                            className="h-8 w-full rounded-md border border-gray-300 bg-white px-2 text-sm disabled:opacity-60"
                            disabled={!isAdmin}
                            value={(m.tee_time_pref ?? g.tee_time_pref) || DEFAULT_TEE_TIME_PREFERENCE}
                            onChange={e => edit(g.id, 'tee_time_pref', e.target.value)}
                            title="Late players are placed in the latest tee times available. One late player sends the whole foursome late."
                          >
                            {TEE_TIME_PREFERENCES.map(p => (
                              <option key={p} value={p}>{TEE_TIME_PREFERENCE_LABELS[p]}</option>
                            ))}
                          </select>
                        </td>
                        <td className="py-1.5 pr-2">
                          <select
                            className="h-8 w-full rounded-md border border-gray-300 bg-white px-2 text-sm disabled:opacity-60"
                            disabled={!isAdmin}
                            value={m.division ?? g.division}
                            onChange={e => edit(g.id, 'division', e.target.value)}
                          >
                            {DIVISIONS.map(d => <option key={d} value={d}>{d}</option>)}
                          </select>
                        </td>
                        <td className="py-1.5 pr-2">
                          <Input className="h-8" type="number" disabled={!isAdmin}
                            value={m.points_adjust ?? g.points_adjust ?? 0}
                            onChange={e => edit(g.id, 'points_adjust', e.target.value)} />
                        </td>
                        {isAdmin && (
                          <td className="py-1.5">
                            <div className="flex items-center gap-1">
                              <Button size="sm" disabled={!dirty(g)} onClick={() => save(g)}>
                                <Save className="h-3.5 w-3.5" />
                              </Button>
                              <Button size="sm" variant="ghost"
                                onClick={() => {
                                  if (window.confirm(`Remove ${g.name} from the roster? Their completed results stay in the record.`)) {
                                    onDeleteGolfer(g.id);
                                  }
                                }}>
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      ))}

      {isAdmin && (
        <>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><ArrowLeftRight className="h-5 w-5" />Replace a player</CardTitle>
              <CardDescription>
                A season-long substitution. The replacement takes over the outgoing player's slot in
                every <strong>unplayed</strong> match, so the round robin stays intact. Results
                already entered stay with the player who actually played them.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-5">
                <div className="sm:col-span-2">
                  <Label className="text-xs">Player leaving</Label>
                  <select
                    className="h-10 w-full rounded-md border border-gray-300 bg-white px-2 text-sm"
                    value={replace.outId}
                    onChange={e => setReplace(r => ({ ...r, outId: e.target.value }))}
                  >
                    <option value="">Select...</option>
                    {DIVISIONS.map(d => (
                      <optgroup key={d} label={d}>
                        {byDivision[d].map(g => (
                          <option key={g.id} value={g.id}>{g.name} ({formatHandicap(g.handicap_index)})</option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                </div>
                <div>
                  <Label className="text-xs">Replacement name</Label>
                  <Input value={replace.name} onChange={e => setReplace(r => ({ ...r, name: e.target.value }))} />
                </div>
                <div>
                  <Label className="text-xs">Index</Label>
                  <Input value={replace.handicap_index} placeholder="12.5 or +1.5"
                    onChange={e => setReplace(r => ({ ...r, handicap_index: e.target.value }))} />
                </div>
                <div className="flex items-end">
                  <Button
                    className="w-full"
                    disabled={!replace.outId || !replace.name.trim()}
                    onClick={async () => {
                      const out = golfers.find(g => g.id === replace.outId);
                      if (!out) return;
                      const incoming = {
                        id: uid('g'),
                        name: replace.name.trim(),
                        division: out.division,
                        handicap_index: parseHandicapInput(replace.handicap_index),
                        ghin_id: replace.ghin_id || null,
                        seed: out.seed,
                        active: true,
                        points_adjust: 0,
                      };
                      await onReplacePlayer(out, incoming);
                      setReplace({ outId: '', name: '', handicap_index: '', ghin_id: '' });
                    }}
                  >
                    <UserPlus className="mr-1 h-4 w-4" /> Replace
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><ArrowLeftRight className="h-5 w-5" />Swap two players between divisions</CardTitle>
              <CardDescription>
                Keeps both divisions at 8. This changes who plays whom, so the schedule must be
                regenerated afterwards.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                {['aId', 'bId'].map((key, i) => (
                  <div key={key}>
                    <Label className="text-xs">Player {i + 1}</Label>
                    <select
                      className="h-10 w-full rounded-md border border-gray-300 bg-white px-2 text-sm"
                      value={swap[key]}
                      onChange={e => setSwap(s => ({ ...s, [key]: e.target.value }))}
                    >
                      <option value="">Select...</option>
                      {DIVISIONS.map(d => (
                        <optgroup key={d} label={d}>
                          {byDivision[d].map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                        </optgroup>
                      ))}
                    </select>
                  </div>
                ))}
                <div className="flex items-end">
                  <Button
                    className="w-full"
                    variant="outline"
                    disabled={!swap.aId || !swap.bId || swap.aId === swap.bId}
                    onClick={async () => {
                      const a = golfers.find(g => g.id === swap.aId);
                      const b = golfers.find(g => g.id === swap.bId);
                      if (!a || !b) return;
                      if (a.division === b.division) {
                        alert('Both players are already in the same division.');
                        return;
                      }
                      if (!window.confirm(
                        `Swap ${a.name} (${a.division}) with ${b.name} (${b.division})?\n\n` +
                        `You will need to regenerate the schedule from the Schedule tab.`)) return;
                      await onSwapDivisions(a, b);
                      setSwap({ aId: '', bId: '' });
                    }}
                  >
                    <ArrowLeftRight className="mr-1 h-4 w-4" /> Swap divisions
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Plus className="h-5 w-5" />Add a player</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-6">
                <div className="sm:col-span-2">
                  <Label className="text-xs">Name</Label>
                  <Input value={adding.name} onChange={e => setAdding(a => ({ ...a, name: e.target.value }))} />
                </div>
                <div>
                  <Label className="text-xs">Division</Label>
                  <select className="h-10 w-full rounded-md border border-gray-300 bg-white px-2 text-sm"
                    value={adding.division}
                    onChange={e => setAdding(a => ({ ...a, division: e.target.value }))}>
                    {DIVISIONS.map(d => <option key={d} value={d}>{d}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="text-xs">Index</Label>
                  <Input value={adding.handicap_index} placeholder="8.1 or +1.5"
                    onChange={e => setAdding(a => ({ ...a, handicap_index: e.target.value }))} />
                </div>
                <div>
                  <Label className="text-xs">Tee time</Label>
                  <select className="h-10 w-full rounded-md border border-gray-300 bg-white px-2 text-sm"
                    value={adding.tee_time_pref}
                    onChange={e => setAdding(a => ({ ...a, tee_time_pref: e.target.value }))}>
                    {TEE_TIME_PREFERENCES.map(p => (
                      <option key={p} value={p}>{TEE_TIME_PREFERENCE_LABELS[p]}</option>
                    ))}
                  </select>
                </div>
                <div className="flex items-end">
                  <Button
                    className="w-full"
                    disabled={!adding.name.trim()}
                    onClick={async () => {
                      await onSaveGolfer({
                        id: uid('g'),
                        name: adding.name.trim(),
                        division: adding.division,
                        handicap_index: parseHandicapInput(adding.handicap_index),
                        ghin_id: adding.ghin_id || null,
                        tee_time_pref: adding.tee_time_pref || DEFAULT_TEE_TIME_PREFERENCE,
                        seed: (byDivision[adding.division]?.length || 0) + 1,
                        active: true,
                        points_adjust: 0,
                      });
                      setAdding({
                        name: '', division: adding.division, handicap_index: '', ghin_id: '',
                        tee_time_pref: DEFAULT_TEE_TIME_PREFERENCE,
                      });
                    }}
                  >
                    <Plus className="mr-1 h-4 w-4" /> Add
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {inactive.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Inactive / substituted players</CardTitle>
            <CardDescription>Kept for the record. They do not appear in standings or the schedule.</CardDescription>
          </CardHeader>
          <CardContent>
            <ul className="space-y-1 text-sm text-gray-600">
              {inactive.map(g => (
                <li key={g.id} className="flex items-center gap-2">
                  <DivisionBadge division={g.division} />
                  <span>{g.name}</span>
                  <span className="text-gray-400">{formatHandicap(g.handicap_index)}</span>
                  {isAdmin && (
                    <Button size="sm" variant="ghost"
                      onClick={() => onSaveGolfer({ ...g, active: true })}>Reactivate</Button>
                  )}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
